/**
 * Hook unificado para a barra lateral do chat.
 *
 * Consome a RPC `get_contact_sidebar_details` que retorna em uma única chamada:
 * - Dados básicos do contato
 * - Lista de tags atribuídas ao contato
 * - Dados completos da conversa (kanban_phase, inbox + channel_type/provider_slug,
 *   assigned_agent, last_inbound_at — essencial para janela 24h)
 * - 6 contadores das seções do menu lateral (substitui `useContactSidebarCounts`)
 *
 * IMPORTANTE — Janela de 24h (WhatsApp Oficial / Instagram):
 *   `conversation.last_inbound_at` continua sendo atualizado em tempo real via
 *   Realtime no `Chat.tsx`/`useChatConversation.ts` (patch de estado local).
 *   Esta RPC fornece apenas o snapshot inicial e em refreshes manuais; o cálculo
 *   da janela 24h continua 100% client-side via `useSessionWindow`.
 *
 * @release v1.0.0 - 2026-04-19
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger condicional do módulo
const log = createLogger('useContactSidebarDetails');

/** Tag atribuída ao contato */
export interface SidebarContactTag {
  id: string;
  name: string;
  color: string | null;
}

/** Contadores das seções do menu lateral */
export interface SidebarCounts {
  contact_tags_count: number;
  deal_tags_count: number;
  activities_count: number;
  quick_actions_count: number;
  other_conversations_count: number;
  tasks_count: number;
}

/** Dados básicos do contato retornados pela RPC */
export interface SidebarContact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  is_saved_to_agenda: boolean | null;
  saved_to_agenda_at: string | null;
  seller_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/** Dados da conversa retornados pela RPC */
export interface SidebarConversation {
  id: string;
  status: string;
  unread_count: number;
  created_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  /** CRÍTICO para janela 24h */
  last_inbound_at: string | null;
  bot_paused: boolean | null;
  bot_paused_at: string | null;
  has_non_campaign_messages: boolean | null;
  kanban_phase: {
    id: string;
    name: string;
    color: string | null;
    // v1.0.5: category passou a ser objeto {id, name, color} (antes era apenas o nome)
    category: {
      id: string;
      name: string;
      color: string | null;
    } | null;
  } | null;
  inbox: {
    id: string;
    name: string;
    /** CRÍTICO: ativa regra de janela 24h no frontend */
    channel_type: string;
    identifier: string | null;
    provider_slug: string | null;
  } | null;
  assigned_agent: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

/** Resposta consolidada da RPC */
export interface ContactSidebarDetails {
  contact: SidebarContact | null;
  contact_tags: SidebarContactTag[];
  conversation: SidebarConversation | null;
  counts: SidebarCounts;
}

/** Parâmetros do hook */
interface UseContactSidebarDetailsParams {
  contactId: string | undefined;
  inboxId: string | undefined;
  /** ID da conversa atual — necessário para retornar dados completos da conversa */
  conversationId?: string | undefined;
  enabled?: boolean;
}

/**
 * Hook que retorna o snapshot consolidado da barra lateral via RPC unificada.
 */
export function useContactSidebarDetails({
  contactId,
  inboxId,
  conversationId,
  enabled = true,
}: UseContactSidebarDetailsParams) {
  return useQuery<ContactSidebarDetails>({
    queryKey: ['contact-sidebar-details', contactId, inboxId, conversationId],
    queryFn: async () => {
      log.debug('Buscando snapshot do sidebar', { contactId, inboxId, conversationId });

      const { data, error } = await supabase.rpc('get_contact_sidebar_details', {
        p_contact_id: contactId!,
        p_inbox_id: inboxId!,
        p_conversation_id: conversationId ?? null,
      });

      if (error) {
        log.error('Erro ao buscar sidebar details', { error });
        throw error;
      }

      // Normaliza payload — garantindo defaults seguros mesmo se RPC retornar null
      const payload = (data ?? {}) as Partial<ContactSidebarDetails>;

      const normalized: ContactSidebarDetails = {
        contact: payload.contact ?? null,
        contact_tags: payload.contact_tags ?? [],
        conversation: payload.conversation ?? null,
        counts: payload.counts ?? {
          contact_tags_count: 0,
          deal_tags_count: 0,
          activities_count: 0,
          quick_actions_count: 0,
          other_conversations_count: 0,
          tasks_count: 0,
        },
      };

      log.debug('Snapshot recebido', {
        hasContact: !!normalized.contact,
        tagsCount: normalized.contact_tags.length,
        hasConversation: !!normalized.conversation,
      });

      return normalized;
    },
    enabled: enabled && !!contactId && !!inboxId,
    // Cache curto — dados de detalhes mudam com frequência (kanban, agente, tags)
    staleTime: 30_000,
  });
}
